import React, { MouseEvent, useCallback, useMemo, useState } from 'react';
import { FCX } from '@datassential/platform-ui-lib';
import cn from 'classnames';
import { getSvgPath } from '@components/SocialLineChart/SocialLineChart.helpers';
import LineChartTooltip from '@components/LineChart/LineChartTooltip/LineChartTooltip';
import { MonthlyDatum } from './RestaurantOpeningsClosings.types';
import {
    CLOSURES_COLOR,
    OPENINGS_COLOR,
    TOTAL_ACTIVE_COLOR,
    BAR_COLOR,
    BAR_FILL_OPACITY,
} from './RestaurantOpeningsClosings.constants';
import {
    getBarRects,
    getBarPath,
    getScale,
    getLinePoints,
    getActiveIndexFromX,
    getYearSeparators,
    getYearLabels,
    formatLeftTick,
    formatRightTick,
    clampPathToBaseline,
} from './OpeningsClosingsChart.helpers';
import './OpeningsClosingsChart.scss';

// 20ms/point → ~1.2s for 60 months (snappy but readable).
const PER_POINT_DURATION = 20;

const PADDING_LEFT = 55;
const PADDING_RIGHT = 55;
const PADDING_TOP = 16;
const PADDING_BOTTOM = 32;
const PLOT_INNER_X = 16;

interface OpeningsClosingsChartProps {
    data: MonthlyDatum[];
    width?: number;
    height?: number;
}

const OpeningsClosingsChart: FCX<OpeningsClosingsChartProps> = ({
    data,
    width = 700,
    height = 240,
    className,
}) => {
    const [activeIndex, setActiveIndex] = useState<number>(-1);

    const plotWidth = width - PADDING_LEFT - PADDING_RIGHT;
    const plotHeight = height - PADDING_TOP - PADDING_BOTTOM;
    const contentWidth = Math.max(0, plotWidth - PLOT_INNER_X * 2);
    const slotWidth = data.length > 0 ? contentWidth / data.length : 0;
    const animationDuration = PER_POINT_DURATION * Math.max(data.length - 1, 1);

    const leftScale = useMemo(
        () => getScale(plotHeight, data.flatMap((d) => [d.openings, d.closures])),
        [plotHeight, data],
    );
    const rightScale = useMemo(
        () => getScale(plotHeight, data.map((d) => d.totalActive)),
        [plotHeight, data],
    );
    const bars = useMemo(
        () => getBarRects(data, contentWidth, plotHeight, rightScale),
        [data, contentWidth, plotHeight, rightScale],
    );
    const { openingsPoints, closuresPoints } = useMemo(
        () => getLinePoints(data, contentWidth, plotHeight, leftScale),
        [data, contentWidth, plotHeight, leftScale],
    );
    const yearSeparators = useMemo(() => getYearSeparators(data), [data]);
    const yearLabels = useMemo(() => getYearLabels(data, contentWidth), [data, contentWidth]);

    const activedatum = activeIndex >= 0 ? data[activeIndex] : null;
    const tooltipX = activeIndex >= 0
        ? PADDING_LEFT + PLOT_INNER_X + slotWidth * activeIndex + slotWidth / 2
        : 0;

    const handleMouseMove = useCallback((e: MouseEvent<SVGRectElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setActiveIndex(getActiveIndexFromX(e.clientX - rect.left, contentWidth, data.length));
    }, [contentWidth, data.length]);

    const handleMouseLeave = useCallback(() => setActiveIndex(-1), []);

    return (
        <div className={cn('OpeningsClosingsChart', className)}>
            <svg
                className="OpeningsClosingsChart__svg"
                width={width}
                height={height}
                viewBox={`0 0 ${width} ${height}`}
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="Restaurant openings and closings chart"
            >
                <g transform={`translate(${PADDING_LEFT}, ${PADDING_TOP})`}>
                    <line className="OpeningsClosingsChart__axis" x1={0} y1={0} x2={0} y2={plotHeight} />
                    <line className="OpeningsClosingsChart__axis" x1={0} y1={plotHeight} x2={plotWidth} y2={plotHeight} />

                    <g transform={`translate(${PLOT_INNER_X}, 0)`}>
                        {bars.map((bar) => (
                            <path
                                key={`bar-${bar.index}`}
                                data-testid="bar"
                                d={getBarPath(bar)}
                                fill={BAR_COLOR}
                                fillOpacity={BAR_FILL_OPACITY}
                                className="OpeningsClosingsChart__bar"
                                style={{ animationDelay: `${bar.index * 4}ms` }}
                            />
                        ))}

                        <path
                            data-testid="line-path-openings"
                            d={clampPathToBaseline(getSvgPath(openingsPoints, true), plotHeight)}
                            stroke={OPENINGS_COLOR}
                            strokeWidth={2}
                            strokeLinejoin="round"
                            strokeLinecap="round"
                            fill="none"
                            pathLength={1}
                            className="OpeningsClosingsChart__line"
                            style={{ '--animation-duration': `${animationDuration}ms` } as React.CSSProperties}
                        />
                        <path
                            data-testid="line-path-closings"
                            d={clampPathToBaseline(getSvgPath(closuresPoints, true), plotHeight)}
                            stroke={CLOSURES_COLOR}
                            strokeWidth={2}
                            strokeLinejoin="round"
                            strokeLinecap="round"
                            fill="none"
                            pathLength={1}
                            className="OpeningsClosingsChart__line"
                            style={{ '--animation-duration': `${animationDuration}ms` } as React.CSSProperties}
                        />

                        {yearSeparators.map((sep) => (
                            <line
                                key={`sep-${sep.year}`}
                                className="OpeningsClosingsChart__separator"
                                x1={slotWidth * sep.index}
                                y1={0}
                                x2={slotWidth * sep.index}
                                y2={plotHeight}
                            />
                        ))}

                        {activeIndex >= 0 && (openingsPoints[activeIndex] || closuresPoints[activeIndex]) && (
                            <>
                                {openingsPoints[activeIndex] && (
                                    <>
                                        <line
                                            x1={openingsPoints[activeIndex].x}
                                            y1={0}
                                            x2={openingsPoints[activeIndex].x}
                                            y2={plotHeight}
                                            stroke="#fff"
                                            strokeOpacity={0.3}
                                            strokeWidth={1}
                                        />
                                        <circle cx={openingsPoints[activeIndex].x} cy={openingsPoints[activeIndex].y}
                                                r={4} fill={OPENINGS_COLOR} stroke="#fff" strokeWidth={1.5}/>
                                    </>
                                )}
                                {closuresPoints[activeIndex] && (
                                    <circle cx={closuresPoints[activeIndex].x} cy={closuresPoints[activeIndex].y} r={4}
                                            fill={CLOSURES_COLOR} stroke="#fff" strokeWidth={1.5}/>
                                )}
                            </>
                        )}

                        <rect
                            data-testid="plot-area"
                            x={0}
                            y={0}
                            width={contentWidth}
                            height={plotHeight}
                            fill="transparent"
                            onMouseMove={handleMouseMove}
                            onMouseLeave={handleMouseLeave}
                        />
                    </g>

                    {leftScale.ticks.map((tick) => (
                        <text key={`left-tick-${tick}`} className="OpeningsClosingsChart__axis-label" x={-6} y={leftScale.getY(tick)} textAnchor="end" dominantBaseline="middle" fill="#fff" fontSize={12}>
                            {formatLeftTick(tick)}
                        </text>
                    ))}
                    {rightScale.ticks.map((tick) => (
                        <text key={`right-tick-${tick}`} className="OpeningsClosingsChart__axis-label" x={plotWidth + 6} y={rightScale.getY(tick)} textAnchor="start" dominantBaseline="middle" fill="#fff" fontSize={12}>
                            {formatRightTick(tick)}
                        </text>
                    ))}
                    <g transform={`translate(${PLOT_INNER_X}, 0)`}>
                        {yearLabels.map(({ year, x }) => (
                            <text key={`year-${year}`} className="OpeningsClosingsChart__axis-label" x={x} y={plotHeight + 20} textAnchor="middle" fill="#fff" fontSize={12}>
                                {year}
                            </text>
                        ))}
                    </g>
                </g>
            </svg>

            {activedatum !== null && (
                <LineChartTooltip
                    title={activedatum.month}
                    rows={[
                        { title: 'Permanent Closures', color: CLOSURES_COLOR, values: [activedatum.closures.toLocaleString('en-US')] },
                        { title: 'New Openings', color: OPENINGS_COLOR, values: [activedatum.openings.toLocaleString('en-US')] },
                        { title: 'Total Active Restaurants', color: TOTAL_ACTIVE_COLOR, values: [activedatum.totalActive.toLocaleString('en-US')] },
                    ]}
                    // Single column group: each row has one value and the tooltip is single-column only.
                    data={[{ title: '', columns: [{ title: '', index: 0 }] }]}
                    x={tooltipX}
                    y={PADDING_TOP + plotHeight / 2}
                    position={tooltipX < width / 2 ? 'right' : 'left'}
                />
            )}
        </div>
    );
};

export default OpeningsClosingsChart;
